import React, { useState } from "react";
import { X, Eye, UserRound, Signature, AlertTriangle, Clock } from "lucide-react";
import { resolveMediaUrl } from "../../../lib/utils";

const formatSignedDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const formatter = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });

  return formatter
    .format(date)
    .replace(",", "")
    .replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());
};

const hasValidSignature = (signature) => {
  if (!signature) return false;
  const value = String(signature).trim();
  if (!value) return false;
  return !["null", "undefined", "none"].includes(value.toLowerCase());
};

const getObservationSignOffUsers = (detail, attempts = []) => {
  const allAttempts =
    attempts && attempts.length > 0
      ? attempts
      : Array.isArray(detail?.attempts)
        ? detail.attempts
        : [];

  // 1. Creator (Raised By)
  const isContractorCreator =
    detail?.creator_type === "CONTRACTOR_CHECKER";
  const creatorLabel = isContractorCreator
    ? "1. Raised By (Contractor Checker)"
    : "1. Raised By ADL";
  const creatorName = detail?.creator_name || "Creator";
  const creatorDesignation =
    detail?.creator_designation ||
    (isContractorCreator ? "Contractor Checker" : "ADL Safety Checker");
  const creatorSignature = detail?.creator_signature || "";
  const creatorSignedAt = detail?.created_at || "";

  // 2. Maker (Rectified By)
  let latestAttemptMakerName = "";
  let latestAttemptMakerSignature = "";
  let latestAttemptMakerSignedAt = "";
  let latestAttemptMakerDesignation = "";

  for (let i = allAttempts.length - 1; i >= 0; i -= 1) {
    const att = allAttempts[i];
    if (!latestAttemptMakerName && att?.claimed_by_safety_officer_name) {
      latestAttemptMakerName = att.claimed_by_safety_officer_name;
    }
    if (!latestAttemptMakerSignature && att?.safety_officer_signature) {
      latestAttemptMakerSignature = att.safety_officer_signature;
    }
    if (!latestAttemptMakerSignedAt && att?.safety_officer_submitted_at) {
      latestAttemptMakerSignedAt = att.safety_officer_submitted_at;
    }
    if (!latestAttemptMakerDesignation && Array.isArray(att?.assignments)) {
      const matchedAsgn = att.assignments.find(
        (asgn) =>
          (detail?.maker_id && String(asgn.safety_officer_id) === String(detail.maker_id)) ||
          ["CLAIMED", "COMPLETED"].includes(asgn.status)
      );
      if (matchedAsgn?.safety_officer_designation) {
        latestAttemptMakerDesignation = matchedAsgn.safety_officer_designation;
      }
    }
  }

  const makerName =
    detail?.maker_name || latestAttemptMakerName || "Maker";
  const makerSignature =
    detail?.maker_signature ||
    detail?.maker_rectify_signature ||
    latestAttemptMakerSignature ||
    "";
  const makerSignedAt =
    detail?.safety_officer_submitted_at ||
    detail?.claimed_at ||
    latestAttemptMakerSignedAt ||
    "";

  // Derive the maker role label dynamically from assignment_role on attempts or contractor presence.
  // CONTRACTOR_MAKER => Contract Engineer / Supervisor
  // MAKER (internal) => Internal Safety Officer
  const makerIsContractor =
    Boolean(detail?.contractor_id) ||
    Boolean(detail?.contractor_name) ||
    Boolean(detail?.name_of_contractor) ||
    allAttempts.some((a) =>
      (a.assignments || []).some(
        (asgn) => asgn.assignment_role === "CONTRACTOR_MAKER"
      )
    );
  const makerLabel = makerIsContractor
    ? "2. Rectified By (Contract Engineer / Supervisor)"
    : "2. Rectified By (Internal Safety Officer)";

  // Check if Contractor Checker is involved
  const hasContractorChecker = Boolean(
    detail?.contractor_checker_name ||
      detail?.contractor_checker_signature ||
      allAttempts.some(
        (a) =>
          a.submission_route === "CONTRACTOR_CHECKER" &&
          (a.checker_name || a.checker_signature)
      ) ||
      (Array.isArray(detail?.rejection_history) &&
        detail.rejection_history.some(
          (r) => r.checker_level === "CONTRACTOR_CHECKER"
        ))
  );

  let contractorCheckerUser = null;
  if (hasContractorChecker) {
    let ccName = detail?.contractor_checker_name || "";
    let ccSignature = detail?.contractor_checker_signature || "";
    let ccSignedAt = detail?.contractor_checker_reviewed_at || "";

    for (let i = allAttempts.length - 1; i >= 0; i -= 1) {
      const att = allAttempts[i];
      if (att.submission_route === "CONTRACTOR_CHECKER") {
        if (!ccName && att.checker_name) ccName = att.checker_name;
        if (!ccSignature && att.checker_signature)
          ccSignature = att.checker_signature;
        if (!ccSignedAt && att.checker_reviewed_at)
          ccSignedAt = att.checker_reviewed_at;
      }
    }

    contractorCheckerUser = {
      role: "contractor_checker",
      label: "3. Verified By (Contractor Checker)",
      name: ccName || "Contractor Checker",
      designation: detail?.contractor_checker_designation || "Contractor Safety",
      signature: ccSignature,
      signedAt: ccSignedAt,
    };
  }

  // Checker (Reviewed By)
  let latestAttemptCheckerName = "";
  let latestAttemptCheckerSignature = "";
  let latestAttemptCheckerSignedAt = "";

  for (let i = allAttempts.length - 1; i >= 0; i -= 1) {
    const att = allAttempts[i];
    if (att.submission_route !== "CONTRACTOR_CHECKER" || !hasContractorChecker) {
      if (!latestAttemptCheckerName && att?.checker_name) {
        latestAttemptCheckerName = att.checker_name;
      }
      if (!latestAttemptCheckerSignature && att?.checker_signature) {
        latestAttemptCheckerSignature = att.checker_signature;
      }
      if (!latestAttemptCheckerSignedAt && att?.checker_reviewed_at) {
        latestAttemptCheckerSignedAt = att.checker_reviewed_at;
      }
    }
  }

  const checkerName =
    detail?.checker_name || latestAttemptCheckerName || "Checker";
  const checkerSignature =
    detail?.checker_signature || latestAttemptCheckerSignature || "";
  const checkerSignedAt =
    detail?.checker_reviewed_at ||
    detail?.approved_at ||
    latestAttemptCheckerSignedAt ||
    "";

  const checkerLabel = hasContractorChecker
    ? "4. Reviewed By (Internal Checker)"
    : "3. Reviewed By (Checker)";

  const users = [
    {
      role: "creator",
      label: creatorLabel,
      name: creatorName,
      designation: creatorDesignation,
      signature: creatorSignature,
      signedAt: creatorSignedAt,
    },
    {
      role: "maker",
      label: makerLabel,
      name: makerName,
      designation: detail?.maker_designation || latestAttemptMakerDesignation || "",
      signature: makerSignature,
      signedAt: makerSignedAt,
    },
  ];

  if (contractorCheckerUser) {
    users.push(contractorCheckerUser);
  }

  users.push({
    role: "checker",
    label: checkerLabel,
    name: checkerName,
    designation:
      detail?.checker_designation ||
      (hasContractorChecker ? "ADL Safety" : "ADL Safety Checker"),
    signature: checkerSignature,
    signedAt: checkerSignedAt,
  });

  return users;
};

const ObservationSignOffRow = ({ user, onView }) => {
  const hasSignature = hasValidSignature(user?.signature);

  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between transition-colors hover:bg-slate-50">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
            hasSignature
              ? "bg-green-100 text-green-700"
              : "bg-slate-200 text-slate-500"
          }`}
        >
          <UserRound className="h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {user.label}
          </p>

          <p className="mt-0.5 break-words text-sm font-bold leading-snug text-slate-900">
            {user.name}
            {user.designation ? (
              <span className="ml-1.5 text-xs font-medium text-slate-500">
                ({user.designation})
              </span>
            ) : null}
          </p>

          {hasSignature && user.signedAt ? (
            <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-500">
              <Clock className="h-3 w-3 text-slate-400" />
              Signed on: {formatSignedDateTime(user.signedAt)}
            </p>
          ) : null}
        </div>
      </div>

      <button
        type="button"
        onClick={() => onView(user)}
        disabled={!hasSignature}
        className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold shadow-sm transition ${
          hasSignature
            ? "border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 cursor-pointer"
            : "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400 opacity-60"
        }`}
      >
        <Eye className="h-3.5 w-3.5" />
        View
      </button>
    </div>
  );
};

const SignatureViewModal = ({ open, user, onClose }) => {
  if (!open || !user) return null;

  const signatureUrl = resolveMediaUrl(user.signature);

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl animate-in zoom-in-95">
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h3 className="text-base font-bold text-slate-900">
              {user.label}
            </h3>
            <p className="mt-0.5 text-sm text-slate-600">
              {user.name}{" "}
              {user.designation ? (
                <span className="text-xs text-slate-400 font-normal">
                  ({user.designation})
                </span>
              ) : null}
            </p>
            {user.signedAt ? (
              <p className="mt-1 text-xs text-slate-500">
                Signed on:{" "}
                <span className="font-medium text-slate-700">
                  {formatSignedDateTime(user.signedAt)}
                </span>
              </p>
            ) : null}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5">
          {signatureUrl ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <img
                src={signatureUrl}
                alt={`${user.label} signature`}
                className="mx-auto max-h-64 w-full object-contain"
              />
            </div>
          ) : (
            <div className="flex min-h-[160px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-center">
              <Signature className="mb-2 h-8 w-8 text-slate-400" />
              <p className="text-sm font-semibold text-slate-700">
                No signature available
              </p>
              <p className="mt-1 text-xs text-slate-400">
                No signature file found for this step.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function SafetyObservationSignOffSection({
  detail,
  attempts = [],
  title = "Sign Off",
  description = "Workflow signatures for this observation.",
  className = "",
}) {
  const [signatureUser, setSignatureUser] = useState(null);

  if (!detail) return null;

  const users = getObservationSignOffUsers(detail, attempts);
  const visibleUsers = users.filter((user) =>
    hasValidSignature(user?.signature),
  );

  let rejectionHistory = [];
  try {
    rejectionHistory = Array.isArray(detail?.rejection_history)
      ? detail.rejection_history
      : JSON.parse(detail?.rejection_history || "[]");
  } catch (e) {
    rejectionHistory = [];
  }

  return (
    <>
      <div
        className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}
      >
        <div className="mb-4">
          <h3 className="text-sm font-bold text-slate-900">{title}</h3>
          {description ? (
            <p className="mt-0.5 text-xs text-slate-500">{description}</p>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {visibleUsers.length > 0 ? (
            visibleUsers.map((user) => (
              <ObservationSignOffRow
                key={user.role}
                user={user}
                onView={setSignatureUser}
              />
            ))
          ) : (
            <p className="text-xs italic text-slate-500 col-span-full">
              No signatures recorded yet.
            </p>
          )}
        </div>

        {rejectionHistory.length > 0 && (
          <div className="mt-6 border-t border-slate-200 pt-4">
            <h4 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-red-600">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Rejection History ({rejectionHistory.length})
            </h4>
            <div className="space-y-2.5">
              {rejectionHistory.map((rej, i) => {
                const stage = String(
                  rej?.checker_level || rej?.stage || "CHECKER"
                )
                  .replace(/_/g, " ")
                  .trim();
                const reason =
                  rej?.reason || rej?.remarks || "No rejection reason provided.";
                const reviewedAt = rej?.reviewed_at || rej?.timestamp || null;

                return (
                  <div
                    key={i}
                    className="rounded-xl border border-red-100 bg-red-50/70 p-3.5"
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-xs font-bold text-red-800">
                        Attempt {rej?.attempt_no || i + 1} - {stage}
                        {rej?.checker_name ? ` (${rej.checker_name})` : ""}
                      </span>
                      {reviewedAt ? (
                        <span className="text-[10px] text-slate-500">
                          {formatSignedDateTime(reviewedAt)}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-slate-700">
                      <span className="font-semibold">Reason:</span> {reason}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <SignatureViewModal
        open={!!signatureUser}
        user={signatureUser}
        onClose={() => setSignatureUser(null)}
      />
    </>
  );
}

export {
  getObservationSignOffUsers,
  ObservationSignOffRow,
  SignatureViewModal,
  hasValidSignature,
  formatSignedDateTime,
};
